import { GeoDiscount } from 'shared/types/cart/Discount';
import { ProductDetails } from 'shared/types/product/Product';
import { GeoPricingTestData } from 'components/ui/organisms/monarch/rules/geoPricing/types';

type GetGeoDiscountParams = {
  data?: GeoPricingTestData;
  country: string;
  products?: ProductDetails;
};

export const getDiscountByCountry = ({
  data,
  country,
  products,
}: GetGeoDiscountParams) => {
  const countryDiscount = data?.geoBasedDiscounts?.find(
    (baseDiscount) => baseDiscount.countryCode === country,
  );

  if (!countryDiscount) {
    return null;
  }

  const partnerGroups = data?.excludedPartnerGroups?.length
    ? data.excludedPartnerGroups
    : undefined;

  let partnerDiscount: GeoDiscount | undefined;

  if (partnerGroups) {
    const matchingGroups = partnerGroups.filter((group) =>
      group.partnerKeys?.some((key) =>
        products?.partners?.some(
          (p) => p.key.toLowerCase() === key.toLowerCase(),
        ),
      ),
    );
    const mostRestrictive = matchingGroups.length
      ? matchingGroups.reduce((prev, curr) =>
          curr.discountPercentage < prev.discountPercentage ? curr : prev,
        )
      : undefined;
    partnerDiscount = mostRestrictive
      ? {
          discountCode: mostRestrictive.discountCode,
          discountPercentage: mostRestrictive.discountPercentage,
          source: 'partner' as const,
        }
      : undefined;
  } else if (data?.excludedPartners) {
    const excludedKeys = data.excludedPartners.partnerKeys?.map((k) =>
      k.toLowerCase(),
    );
    const hasPartnerDiscount = products?.partners?.some((partner) =>
      excludedKeys?.includes(partner?.key.toLowerCase()),
    );
    partnerDiscount = hasPartnerDiscount
      ? {
          discountCode: data.excludedPartners.discountCode,
          discountPercentage: data.excludedPartners.discountPercentage,
          source: 'partner' as const,
        }
      : undefined;
  }

  const excludedProductTypes = data?.excludedProductTypes;
  const hasProductTypeDiscount = excludedProductTypes?.productTypes.some(
    (type) => type.toLowerCase() === products?.productType?.toLowerCase(),
  );

  const productTypeDiscount: GeoDiscount | undefined =
    hasProductTypeDiscount && excludedProductTypes
      ? {
          discountCode: excludedProductTypes.discountCode,
          discountPercentage: excludedProductTypes.discountPercentage,
          source: 'product' as const,
        }
      : undefined;

  return (
    partnerDiscount ??
    productTypeDiscount ?? { ...countryDiscount, source: 'country' as const }
  );
};
